import React, { useId } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { Verified } from "@mui/icons-material";
import { TIER_DISPLAY, TIERS } from "../../constants/membership";
import TooltipComponent from "../common/Tooltip";

export function resolveMembershipDisplay(player, { compact = false } = {}) {
    const tier = (player?.membershipTier || player?.membership?.tier || TIERS.FREE)
        .toString()
        .trim()
        .toLowerCase();

    if (tier === TIERS.FREE) return null;

    const display = TIER_DISPLAY[tier] || TIER_DISPLAY[TIERS.FREE];
    const label = compact
        ? display?.badge || display?.profileBadge
        : display?.profileBadge || display?.badge;
    if (!label) return null;

    return { label, color: display.color };
}

export function isOuroVerifiedAccount(player) {
    return (player?.accountStatus || "")
        .toString()
        .trim()
        .toLowerCase() === "verified";
}

const VERIFIED_SIZES = {
    compact: "0.95rem",
    default: { xs: "1.25rem", xl: "1.4rem" },
    hero: { xs: "1.55rem", md: "1.85rem" },
};

export function OuroVerifiedBadge({ gradientId, size = "default" }) {
    const fontSize = VERIFIED_SIZES[size] || VERIFIED_SIZES.default;

    return (
        <TooltipComponent title="Ouro verified" placement="top">
            <Box
                component="span"
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    lineHeight: 0,
                    position: "relative",
                    filter: `
                        drop-shadow(0 1px 0 rgba(255,255,255,0.18))
                        drop-shadow(0 3px 10px rgba(246,185,76,0.22))
                    `,
                    "& svg": {
                        fontSize,
                        fill: `url(#${gradientId})`,
                    },
                }}
            >
                <svg width="0" height="0" aria-hidden="true" style={{ position: "absolute" }}>
                    <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#FFF4CC" />
                            <stop offset="18%" stopColor="#F6D67A" />
                            <stop offset="48%" stopColor="#F6B94C" />
                            <stop offset="100%" stopColor="#C97A16" />
                        </linearGradient>
                    </defs>
                </svg>
                <Verified />
            </Box>
        </TooltipComponent>
    );
}

OuroVerifiedBadge.propTypes = {
    gradientId: PropTypes.string.isRequired,
    size: PropTypes.oneOf(["compact", "default", "hero"]),
};

const RANK_SCOPE_LABELS = {
    global: "Global",
    country: "Country",
    friends: "Friends",
};

function getRankScopeLabel(scope) {
    return RANK_SCOPE_LABELS[scope] || RANK_SCOPE_LABELS.global;
}

export function ProfileRankBadge({ rank, size = "default", scope = "global" }) {
    const gradientId = "profile-rank-" + useId().replace(/:/g, "");
    const numericRank = Number(rank);
    if (!Number.isFinite(numericRank) || numericRank < 1 || numericRank > 1000) return null;
    const compact = size === "compact";
    const isSecondPlace = numericRank === 2;
    const isThirdPlace = numericRank === 3;
    const isStandardRank = numericRank >= 4;
    const capsuleBorder = isStandardRank
        ? "rgba(92,124,158,.54)"
        : isSecondPlace
          ? "rgba(255,255,255,.72)"
          : isThirdPlace
            ? "rgba(255,191,139,.68)"
            : "rgba(255,221,139,.66)";
    const capsuleBackground = isStandardRank
        ? "radial-gradient(ellipse at 30% 5%, rgba(239,250,255,.62) 0%, rgba(126,199,250,.28) 40%, transparent 64%), linear-gradient(180deg, #9BC5E5 0%, #6E9FC7 34%, #47789F 70%, #2F5577 100%)"
        : isSecondPlace
          ? "linear-gradient(180deg, #FFFFFF 0%, #E5E9EE 30%, #B9C0C8 70%, #7D858E 100%)"
          : isThirdPlace
            ? "linear-gradient(180deg, #FFD3AD 0%, #E89150 31%, #B85E2D 70%, #71351B 100%)"
            : "linear-gradient(180deg, #FFE39A 0%, #F4BA43 32%, #D79216 72%, #A9660C 100%)";
    const capsuleShadow = isStandardRank
        ? "inset 0 1px 0 rgba(241,251,255,.68), inset 0 -2px 4px rgba(7,20,34,.42), 0 3px 9px rgba(0,0,0,.32), 0 0 12px rgba(91,174,233,.3)"
        : isSecondPlace
          ? "inset 0 1px 0 rgba(255,255,255,.92), inset 0 -2px 4px rgba(29,35,42,.34), 0 4px 12px rgba(0,0,0,.34), 0 0 12px rgba(218,226,235,.1)"
          : isThirdPlace
            ? "inset 0 1px 0 rgba(255,240,226,.64), inset 0 -2px 4px rgba(67,24,8,.42), 0 4px 12px rgba(0,0,0,.34), 0 0 12px rgba(205,103,50,.12)"
            : "inset 0 1px 0 rgba(255,255,255,.58), inset 0 -2px 4px rgba(77,38,0,.34), 0 4px 12px rgba(0,0,0,.34), 0 0 12px rgba(232,163,61,.12)";
    const emblemBorder = isStandardRank
        ? "rgba(151,204,241,.82)"
        : isSecondPlace
          ? "rgba(238,244,250,.78)"
          : isThirdPlace
            ? "rgba(255,180,129,.74)"
            : "rgba(255,211,103,.72)";
    const emblemShadow = isStandardRank
        ? "inset 0 1px 1px rgba(255,255,255,.06), inset 0 -2px 3px rgba(0,0,0,.64), 0 0 5px rgba(92,124,158,.1)"
        : isSecondPlace
          ? "inset 0 1px 1px rgba(255,255,255,.12), inset 0 -2px 3px rgba(0,0,0,.68), 0 1px 2px rgba(255,255,255,.3)"
          : isThirdPlace
            ? "inset 0 1px 1px rgba(255,211,177,.12), inset 0 -2px 3px rgba(0,0,0,.68), 0 1px 2px rgba(255,178,126,.24)"
            : "inset 0 1px 1px rgba(255,255,255,.09), inset 0 -2px 3px rgba(0,0,0,.66), 0 1px 2px rgba(255,236,177,.22)";
    const emblemGradientId = gradientId;
    const emblemGradientTop = isStandardRank ? "#B8D0E6" : isSecondPlace ? "#FFFFFF" : isThirdPlace ? "#FFE2C8" : "#FFF5C7";
    const emblemGradientMiddle = isStandardRank ? "#7FA5C8" : isSecondPlace ? "#E3E9F0" : isThirdPlace ? "#F2A064" : "#FFD669";
    const emblemGradientBottom = isStandardRank ? "#527696" : isSecondPlace ? "#9BA6B2" : isThirdPlace ? "#B9562C" : "#D88A12";
    const emblemGlow = isStandardRank ? "rgba(126,165,200,.36)" : isSecondPlace ? "rgba(239,245,252,.42)" : isThirdPlace ? "rgba(242,140,78,.42)" : "rgba(255,199,72,.44)";
    const scopeLabel = getRankScopeLabel(scope);

    return (
        <TooltipComponent title={`${scopeLabel} rank #${numericRank}`} placement="top">
            <Box
                component="span"
                aria-label={`${scopeLabel} rank ${numericRank}`}
                sx={{
                    position: "relative",
                    display: "inline-flex",
                    alignItems: "center",
                    height: compact ? "1.55rem" : { xs: "1.7rem", md: "1.9rem" },
                    minWidth: compact ? "3.55rem" : { xs: "3.95rem", md: "4.35rem" },
                    pl: compact ? "0.62rem" : { xs: "0.7rem", md: "0.78rem" },
                    pr: compact ? "0.16rem" : "0.2rem",
                    gap: compact ? "0.38rem" : "0.48rem",
                    flexShrink: 0,
                    overflow: "hidden",
                    borderRadius: "999px",
                    border: "1px solid " + capsuleBorder,
                    background: capsuleBackground,
                    boxShadow: capsuleShadow,
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        left: "9%",
                        right: "17%",
                        top: "2px",
                        height: "42%",
                        borderRadius: "999px",
                        background: isStandardRank ? "linear-gradient(180deg, rgba(255,255,255,.16), rgba(255,255,255,0))" : "linear-gradient(180deg, rgba(255,255,255,.34), rgba(255,255,255,0))",
                        pointerEvents: "none",
                    },
                }}
            >
                <Typography component="span" sx={{ position: "relative", zIndex: 1, flex: "1 1 auto", minWidth: 0, textAlign: "center", color: "#201507", fontFamily: "'JetBrains Mono', monospace", fontSize: compact ? "0.72rem" : { xs: "0.78rem", md: "0.86rem" }, fontWeight: 900, lineHeight: 1, letterSpacing: "-.06em", textShadow: "0 1px 0 rgba(255,255,255,.22)" }}>
                    #{numericRank}
                </Typography>
                <Box component="span" sx={{ position: "relative", zIndex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", width: compact ? "1.3rem" : { xs: "1.44rem", md: "1.64rem" }, height: compact ? "1.3rem" : { xs: "1.44rem", md: "1.64rem" }, ml: "auto", flexShrink: 0, borderRadius: "50%", background: isStandardRank ? "radial-gradient(circle at 42% 32%, #334355 0%, #18222D 62%, #0C1219 100%)" : "radial-gradient(circle at 42% 32%, #3B352A 0%, #17140F 62%, #080705 100%)", border: "1px solid " + emblemBorder, boxShadow: emblemShadow + ", 0 0 7px " + emblemGlow }}>
                    <Box component="svg" viewBox="0 0 32 32" aria-hidden="true" sx={{ width: "94%", height: "94%", overflow: "visible", filter: "drop-shadow(0 0 1.5px " + emblemGlow + ")" }}>
                        <defs>
                            <linearGradient id={emblemGradientId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={emblemGradientTop} />
                                <stop offset="38%" stopColor={emblemGradientMiddle} />
                                <stop offset="100%" stopColor={emblemGradientBottom} />
                            </linearGradient>
                        </defs>
                        <g fill="none" stroke={"url(#" + emblemGradientId + ")"} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11.6 25.3C7.2 23 5 19.4 5.4 14.6M9.5 23.8l-2.9.2M7.6 21.5l-2.8-.9M6.3 18.5l-2.3-1.7M6.1 15.4l-1.6-2.3" strokeWidth="1.65" />
                            <path d="M20.4 25.3c4.4-2.3 6.6-5.9 6.2-10.7M22.5 23.8l2.9.2M24.4 21.5l2.8-.9M25.7 18.5l2.3-1.7M25.9 15.4l1.6-2.3" strokeWidth="1.65" />
                            <circle cx="16" cy="15.2" r="5.35" strokeWidth="1.75" />
                            <circle cx="16" cy="15.2" r="2.25" fill={"url(#" + emblemGradientId + ")"} stroke="none" />
                            <path d="M16 7.1v2.25M16 21.05v2.25M7.9 15.2h2.25M21.85 15.2h2.25" strokeWidth="1.65" />
                        </g>
                    </Box>
                </Box>
            </Box>
        </TooltipComponent>
    );
}

ProfileRankBadge.propTypes = {
    rank: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    size: PropTypes.oneOf(["compact", "default"]),
    scope: PropTypes.oneOf(["global", "country", "friends"]),
};

const MEMBERSHIP_BADGE_SIZES = {
    default: {
        py: "5px",
        px: "11px",
        clipInset: "5px",
        fontSize: "0.6875rem",
        letterSpacing: "0.1em",
    },
    compact: {
        py: "3px",
        px: "8px",
        clipInset: "4px",
        fontSize: "0.55rem",
        letterSpacing: "0.1em",
    },
};

export function MembershipBadge({ label, size = "default" }) {
    const sizing = MEMBERSHIP_BADGE_SIZES[size] || MEMBERSHIP_BADGE_SIZES.default;
    const clipInset = sizing.clipInset;

    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                alignSelf: "center",
                py: sizing.py,
                px: sizing.px,
                borderRadius: "3px",
                border: "1px solid rgba(246,199,107,0.4)",
                clipPath: `polygon(${clipInset} 0, 100% 0, calc(100% - ${clipInset}) 100%, 0 100%)`,
                background: `
                    linear-gradient(
                        180deg,
                        rgba(35,28,18,0.7) 0%,
                        rgba(18,14,8,0.85) 100%
                    )
                `,
            }}
        >
            <Typography
                component="span"
                sx={{
                    fontSize: sizing.fontSize,
                    fontWeight: 700,
                    letterSpacing: sizing.letterSpacing,
                    textTransform: "uppercase",
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                    color: "#F6D28B",
                }}
            >
                {label}
            </Typography>
        </Box>
    );
}

MembershipBadge.propTypes = {
    label: PropTypes.string.isRequired,
    size: PropTypes.oneOf(["default", "compact"]),
};
